// Home (rebuild.md §4.9): key art with the Home background loop, title, Continue, the menu and the tier
// selector (three tiers + locked Tier-0) with a one-line description of each.
import { useEffect } from 'preact/hooks';
import { audio } from '@audio/engine.ts';
import { blackoutUnlocked, echoUpgradeUnlocked, hasFlag, isFiled, isUnlocked, mondayUnlocked, reducedMotion, save, setFlag, setTier, tier, totalCommendations } from '@app/state.ts';
import { installAvailable, promptInstall } from '@app/install.ts';
import { CHAPTERS, chapter } from '@content/chapters.ts';
import { hasStage } from '@content/stages/index.ts';
import { TIERS } from '@prompts/types.ts';
import { TIER_DEFS } from '@stage/tiers.ts';
import { Art } from '@ui/Art.tsx';
import { Bubble } from '@ui/characters.tsx';
import { Button } from '@ui/core.tsx';
import { useNavigate } from './Shell.tsx';
import './screens.css';

export function continueTarget(): { href: string; label: string } | null {
  const cp = save.value.checkpoint;
  if (cp && hasStage(cp.stageId)) {
    const c = chapter(cp.stageId);
    return { href: `/stage/${cp.stageId}`, label: `Continue: ${c?.stage ?? cp.stageId}, goal ${cp.goalIndex + 1}` };
  }
  const next = CHAPTERS.find((c) => c.built && hasStage(c.id) && isUnlocked(c.id) && !isFiled(c.id));
  if (next) return { href: `/stage/${next.id}`, label: isFiled('prologue') ? `Continue: ${next.label.toLowerCase().replace(/^\w/, (x) => x.toUpperCase())}` : 'Begin the Prologue' };
  return null;
}

export function Home() {
  const navigate = useNavigate();
  const cont = continueTarget();
  const motion = !reducedMotion();
  useEffect(() => {
    void audio.music('m00_caper', { loop: true, fade: 2 });
  }, []);
  return (
    <div class="home">
      <div class="home-bg" aria-hidden="true">
        {motion ? (
          <video src="/loops/home.mp4" poster="/loops/home.webp" autoPlay muted loop playsInline preload="auto" onError={(e) => ((e.target as HTMLVideoElement).style.display = 'none')} />
        ) : null}
        {!motion && <Art id="home-wide" decorative eager sizes="100vw" />}
      </div>
      <main id="main" class="home-panel">
        <h1 class="home-title">
          Forge &amp; Co.
          <span>Last Reagent</span>
        </h1>
        <p class="home-sub">A chemistry game in seventeen chapters</p>
        <nav class="home-menu" aria-label="Main menu">
          {cont && (
            <Button
              tone="primary"
              big
              class="home-continue"
              onClick={() => {
                audio.unlock();
                navigate(cont.href);
              }}
            >
              ▶ {cont.label}
            </Button>
          )}
          <Button onClick={() => navigate('/chapters')}>Chapters</Button>
          <Button onClick={() => navigate('/theatre')}>Theatre</Button>
          <Button onClick={() => navigate('/archive')}>Archive</Button>
          <Button onClick={() => navigate('/notebook')}>Notebook</Button>
          <Button onClick={() => navigate('/practice')}>Lab Practice</Button>
          {mondayUnlocked.value && <Button onClick={() => navigate('/monday')}>Monday</Button>}
          {echoUpgradeUnlocked.value && <Button onClick={() => navigate('/echo-upgrade')}>ECHO upgrade allocation</Button>}
          <Button onClick={() => navigate('/settings')}>Settings</Button>
          {installAvailable.value && (
            <Button tone="ghost" onClick={() => void promptInstall()}>
              ⤓ Install the game
            </Button>
          )}
        </nav>
        {blackoutUnlocked.value && !hasFlag('blackout-unlocked') && (
          <div class="home-notice" role="status">
            <Bubble who="mei" text="tier-0. blackout. officially it doesn’t exist. no hints, hard timers, no formula sheet. you didn’t hear it from me." />
            <Button
              tone="primary"
              onClick={() => {
                setFlag('blackout-unlocked');
                setTier('blackout');
              }}
            >
              Switch to Tier-0
            </Button>
            <Button tone="ghost" onClick={() => setFlag('blackout-unlocked')}>
              Not yet
            </Button>
          </div>
        )}
        <fieldset class="tier-picker">
          <legend>Difficulty</legend>
          <div class="tier-options" role="radiogroup" aria-label="Difficulty tier">
            {TIERS.map((t) => {
              const d = TIER_DEFS[t];
              const locked = t === 'blackout' && !blackoutUnlocked.value && !save.value.classroom;
              return (
                <button
                  key={t}
                  type="button"
                  role="radio"
                  aria-checked={tier.value === t}
                  class="tier-option"
                  disabled={locked}
                  onClick={() => setTier(t)}
                >
                  <span class="tier-name">
                    {d.label}
                    {locked && ' 🔒'}
                  </span>
                  <span class="tier-desc">{locked ? `Unlocks at ${d.unlock} commendations.` : d.description}</span>
                </button>
              );
            })}
          </div>
        </fieldset>
        <div class="home-meta">
          <span>★ {totalCommendations.value} / 51 commendations</span>
          {save.value.classroom && <span class="tag">Classroom mode</span>}
        </div>
        <p class="home-version">v{__APP_VERSION__}</p>
      </main>
    </div>
  );
}
